import React from 'react';
import {
  ChatCircleIcon,
  CalendarDotsIcon,
  DotsThreeCircleIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { useNavigate } from '@tanstack/react-router';
import { InitialsBadge } from '../InitialsBadge';
import { Pill } from '../ui/pill';

export interface PostProps {
  postId: number;
  author: string;
  postTimestamp: string;
  imgUrl?: string;
  content: string;
  commentCount: number;
  isAdminOrOwner: boolean;
  disableClick?: boolean;
  showActionBar?: boolean;
  onMenuClick?: () => void;
  moderationStatus?: 'pending' | 'approved' | 'rejected';
}

export function Post({
  postId,
  author,
  postTimestamp,
  imgUrl,
  content,
  commentCount,
  isAdminOrOwner,
  disableClick = false,
  showActionBar = true,
  onMenuClick,
  moderationStatus,
}: PostProps) {
  const navigate = useNavigate();

  function renderTimeStamp() {
    return (
      <div className="flex items-center gap-1">
        <CalendarDotsIcon size={20} weight="regular" />
        <span className="text-slate-400 text-small">{postTimestamp}</span>
      </div>
    );
  }

  function handleClick() {
    if (!disableClick) {
      navigate({
        to: '/feed/$postId',
        params: { postId: postId.toString() },
      });
    }
  }

  function handleMenuClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    onMenuClick?.();
  }

  return (
    <div
      onClick={handleClick}
      className={`w-full flex flex-col text-left py-4 first:pt-0 last:pb-0 ${disableClick ? '' : 'cursor-pointer'}`}
      role={disableClick ? undefined : 'button'}
      tabIndex={disableClick ? undefined : 0}
      onKeyDown={
        disableClick
          ? undefined
          : (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClick();
              }
            }
      }
    >
      <div className="flex flex-col gap-2 pt-4">
        {/* Moderation Status Badge */}
        {moderationStatus === 'pending' && (
          <div className="self-start">
            <Pill variant="warning" icon="⏳">
              Pending Approval
            </Pill>
          </div>
        )}
        {moderationStatus === 'rejected' && (
          <div className="self-start">
            <Pill variant="error" icon={<WarningCircleIcon size={16} />}>
              Not Approved
            </Pill>
          </div>
        )}

        {/* Title */}
        <div className="flex justify-between w-full">
          <div className="flex items-center gap-2">
            <InitialsBadge name={author} badgeClassName="text-slate-400" />
            <span className="text-large">{author}</span>
          </div>
          {renderTimeStamp()}
        </div>

        {/* Image */}
        {imgUrl && (
          <div className="w-full rounded-lg overflow-hidden">
            <img
              src={imgUrl}
              alt="Post"
              className="w-full h-full object-contain"
            />
          </div>
        )}

        {/* Content */}
        <div className="flex flex-col">
          <span className="break-words [overflow-wrap:anywhere]">{content}</span>
        </div>

        {/* Action Bar */}
        {showActionBar && (
          <div className="flex justify-between w-full mt-4">
            <div className="flex items-center gap-1">
              <ChatCircleIcon
                size={20}
                weight="regular"
                className="text-sky-400"
              />
              <span>{commentCount}</span>
            </div>

            {isAdminOrOwner && (
              <button
                type="button"
                onClick={handleMenuClick}
                className="flex items-center gap-1 cursor-pointer"
                aria-label="Post menu"
              >
                <DotsThreeCircleIcon size={20} weight="regular" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
